/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Seo, Footer } from '@components/systems'
import { mono, monoShared } from '@components/systems/monolith/monolith.tokens'
import { softwareList } from '@src/configs/software.config'

import studioImg from '@public/images/monolith/studio-switch-presets.jpg'

import { useNavigate } from 'react-router-dom'

/**
 * 코드 → 목록 썸네일. 새 소프트웨어가 생기면 여기에 한 줄 추가.
 * 상세의 리드샷과 같은 화면을 쓴다 — 목록에서 본 것이 그대로 열린다.
 */
const thumbnailObj: { [code: string]: string } = {
	studio: studioImg,
}

/**
 * Software 목록 — `/software` (PENDING_CHANGES §12).
 * Shop 의 행 레이아웃과 같은 문법: 왼쪽 스크린샷, 오른쪽 이름·설명·플랫폼·링크.
 */
const MonolithSoftwarePage = () => {
	const navigate = useNavigate()

	return (
		<View css={style}>
			<Seo />

			<View className="mnWrap">
				<View className="mnPageHeader">
					<h1 className="mnPageTitle">Software</h1>
					<p className="mnPageLede">Desktop software for Judahs gear.</p>
				</View>

				{softwareList.map(aSoftware => {
					const thumbnail = thumbnailObj[aSoftware.code]
					const path = `/software/${aSoftware.slug}`
					return (
						<View key={aSoftware.slug} className="mnBand mnRow">
							<a
								className="mnRowImageLink"
								href={path}
								onClick={e => {
									e.preventDefault()
									navigate(path)
								}}
							>
								{thumbnail !== undefined && (
									<img src={thumbnail} alt={aSoftware.name} className="mnRowImage" />
								)}
							</a>
							<View className="mnRowText">
								<a
									className="mnRowTitle"
									href={path}
									onClick={e => {
										e.preventDefault()
										navigate(path)
									}}
								>
									{aSoftware.name}
								</a>
								<p className="mnRowBody">
									{aSoftware.tagline} {aSoftware.description}
								</p>
								<View className="mnRowActions">
									<button
										type="button"
										className="mnCta mnRowCta"
										onClick={() => navigate(path)}
									>
										Downloads
									</button>
									<span className="mnRowMeta">
										{aSoftware.platforms
											.map(aPlatform => aPlatform.osName)
											.filter((v, i, arr) => arr.indexOf(v) === i)
											.join(' · ')}
									</span>
								</View>
							</View>
						</View>
					)
				})}
			</View>

			<Footer />
		</View>
	)
}

export default MonolithSoftwarePage

const style = css({
	...monoShared,
	position: 'relative',
	width: '100%',

	// 페이지 헤더 — 1열. 제목은 전폭, 소제목은 그 아래(DESIGN_SYSTEM §6.2). 4:8 은 두 단어 제목(Works with)이 줄바꿈돼 폐기.
	'.mnPageHeader': {
		padding: 'clamp(64px, 8.3vw, 120px) 0 clamp(48px, 6.25vw, 90px)',

		'.mnPageTitle': {
			fontSize: 'clamp(56px, 7.6vw, 110px)',
			lineHeight: 1,
			fontWeight: 100,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},
		'.mnPageLede': {
			fontSize: 19,
			lineHeight: 1.6,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 520,
			marginTop: 'clamp(20px, 2vw, 28px)',
		},
	},

	'.mnRow': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 560px) minmax(0, 1fr)',
		gap: 'clamp(32px, 6.6vw, 96px)',
		alignItems: 'center',
		padding: 'clamp(48px, 6.25vw, 90px) 0',
		borderBottom: `1px solid ${mono.line}`,
		marginBottom: 'clamp(64px, 8.3vw, 120px)',

		'.mnRowImageLink': {
			display: 'block',
			width: '100%',

			'.mnRowImage': {
				width: '100%',
				display: 'block',
				border: `1px solid ${mono.line}`,
				borderRadius: 8,
			},
		},

		'.mnRowText': {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: 22,
		},

		'.mnRowTitle': {
			fontSize: 'clamp(28px, 2.8vw, 40px)',
			fontWeight: 300,
			lineHeight: 1.1,
			color: mono.ink,
		},

		'.mnRowBody': {
			fontSize: 16,
			lineHeight: 1.7,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 520,
		},

		'.mnRowActions': {
			display: 'flex',
			alignItems: 'center',
			gap: 28,
			marginTop: 6,
			flexWrap: 'wrap',

			'.mnRowCta': {
				height: 50,
				padding: '0 36px',
				fontSize: 14,
			},
			'.mnRowMeta': {
				fontSize: 13,
				letterSpacing: '.18em',
				textTransform: 'uppercase',
				color: mono.faint,
				fontWeight: 500,
			},
		},

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 32,
		},
	},
})
